// Libs
import classNames from 'classnames/bind';
import { useCallback, useEffect, useState } from 'react';
import {
  Avatar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2,
  IconButton,
  Stack,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
// Components, Layouts, Pages
import { BaseDatePicker, BaseInput, BaseLabel, BaseSelect, BaseUploadDocument } from '~/components/common';
// Others
import { defaultValuesQuarantineForm, schema } from './helper';
import { IBaseFile, IBaseOption, IBodyUpdate, IPayloadCallApiFn, IResultCallApiFn } from '~/utils/interface/common';
import {
  DEFAULT_LIMIT_PAGE,
  DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA,
  EMPTY_STRING,
  optionQuarantineType,
} from '~/utils/constants/common';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAppDispatch } from '~/utils/redux/hooks';
import { IFormQuarantine } from '~/utils/interface/quarantine';
import { getListInventoryProduct } from '~/thunks/inventory/inventoryThunk';
import { IInventoryProduct, IParamInventoryProduct } from '~/utils/interface/inventory';
import { createQuarantine, getDetailQuarantine, updateQuarantine } from '~/thunks/quarantine/quarantineThunk';
import { compareDataUpdate, createFormData, removeEmptyObjects } from '~/utils/helper/common';
import { quarantineActions } from '~/thunks/quarantine/quarantineSlice';
import { IOptionProductItem } from '~/utils/interface/purchaseOrder';
// Styles, images, icons
import styles from './QuarantineForm.module.scss';
import { icons } from '~/assets';

type Props = {
  idQuarantine?: string;
  onClose: () => void;
  onRemoveFile?: (id: string) => void;
};

const cx = classNames.bind(styles);

const QuarantineFormModal = (props: Props) => {
  //#region Destructuring Props
  const { idQuarantine, onClose, onRemoveFile } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/quarantine', 'common/base']);
  const dispatch = useAppDispatch();
  const {
    control,
    setValue,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isDirty },
  } = useForm<IFormQuarantine>({
    resolver: yupResolver(schema(t)),
    defaultValues: defaultValuesQuarantineForm,
  });

  const { variantId } = watch();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [initialData, setInitialData] = useState<IFormQuarantine>(defaultValuesQuarantineForm);
  const [productNameDefault, setProductNameDefault] = useState<string>(EMPTY_STRING);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!idQuarantine) return;

    handleGetDetailQuarantine(idQuarantine);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idQuarantine]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleCloseModal = () => {
    onClose && onClose();
    reset();
  };

  const handleFormQuarantine = (data: IFormQuarantine) => {
    if (idQuarantine) {
      handleEditQuarantine(data);
    } else {
      handleAddQuarantine(data);
    }
  };

  const handleAddQuarantine = async (data: IFormQuarantine) => {
    const { productId, ...newObj } = data;

    const dataConverted: IFormQuarantine = {
      ...newObj,
      inventoryId: productId || EMPTY_STRING,
      ...(variantId && { variantId }),
    };

    const formData = await createFormData(removeEmptyObjects(dataConverted), ['quarantineFile']);

    dispatch(createQuarantine(formData))
      .unwrap()
      .then((_res) => {
        dispatch(quarantineActions.setRefreshQuarantineList(false));
        handleCloseModal();
      })
      .catch((_error) => {})
      .finally(() => {});
  };

  const handleEditQuarantine = async (data: IFormQuarantine) => {
    const { variantId, productId, inventoryId, ...remainingData } = data;
    const dataChange = compareDataUpdate(initialData, remainingData) as IFormQuarantine;

    const formData = await createFormData(dataChange, ['quarantineFile']);

    const params: IBodyUpdate<FormData> = {
      id: idQuarantine || EMPTY_STRING,
      body: formData,
    };

    dispatch(updateQuarantine(params))
      .unwrap()
      .then(() => {
        dispatch(quarantineActions.setRefreshQuarantineList(false));
        handleCloseModal();
      })
      .catch(() => {})
      .finally(() => {});
  };

  const fetchOptionsProduct = useCallback(
    async (payload: IPayloadCallApiFn): Promise<IResultCallApiFn<IOptionProductItem>> => {
      const { page, searchKey } = payload;

      const payloadParams: IParamInventoryProduct = {
        page: page,
        limit: DEFAULT_LIMIT_PAGE,
        isHasVariant: true,
        ...(searchKey && { textSearch: searchKey }),
      };

      try {
        const resp = await dispatch(getListInventoryProduct(payloadParams)).unwrap();

        const { responses: listProduct, pagination } = resp?.data;

        const formatOptions: IOptionProductItem[] = handleConvertOptionProduct(listProduct);

        const hasMore: boolean = pagination?.page < pagination?.totalPages || false;

        return { optionsResp: formatOptions, hasMoreResp: false };
      } catch (error) {
        return DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA;
      }
    },
    []
  );

  const handleConvertOptionProduct = (data: IInventoryProduct[]): IOptionProductItem[] => {
    const options: IOptionProductItem[] = data.map((item) => ({
      value: `${item.id}_${item.variantId}`,
      productId: item.id,
      label: `${item.name} ${item?.variantName ? ` - ${item?.variantName}` : EMPTY_STRING}`,
      price: item.rentalPrice,
      variantId: item?.variantId,
    }));

    return options;
  };

  const handleRemoveFile = (file: IBaseFile) => {
    if (!file.id) return;

    onRemoveFile?.(file.id);
  };

  const fetchOptionsQuarantineType = useCallback(async (): Promise<IResultCallApiFn<IBaseOption>> => {
    const result: IResultCallApiFn<IBaseOption> = {
      optionsResp: optionQuarantineType,
      hasMoreResp: false,
    };

    return result;
  }, []);

  const handleGetDetailQuarantine = (idQuarantine: string) => {
    dispatch(getDetailQuarantine(idQuarantine))
      .unwrap()
      .then((res) => {
        if (!res?.data) return;

        const { id, fileQuarantine, inventoryId, ...remainingData } = res?.data;
        const body: IFormQuarantine = {
          ...remainingData,
          quarantineFile: fileQuarantine.map((item) => {
            return item ? { id: item.id, url: item.url, file: item as any } : {};
          }),
          inventoryId: `${inventoryId}_${remainingData.variantId}`,
        };

        setProductNameDefault(
          `${res?.data?.inventoryName} ${res?.data?.variantName ? ` - ${res?.data?.variantName}` : EMPTY_STRING}`
        );

        setInitialData(body);

        reset(body);
      })
      .catch((_error) => {})
      .finally(() => {});
  };
  //#endregion Handle Function

  return (
    <Dialog
      id='QuarantineFormModalComponent'
      aria-labelledby='QuarantineFormModal'
      open={true}
      onClose={handleCloseModal}
      fullWidth
      maxWidth={'md'}
    >
      <DialogTitle id='trailerFormModal' className={cx('header')}>
        <Stack direction={'column'} spacing={'10px'}>
          <Stack direction={'row'} spacing={10}>
            <BaseLabel
              label={t(idQuarantine ? 'form_add_quarantine_title_edit' : 'form_add_quarantine_title')}
              styleType='bigTitle'
            />
          </Stack>
        </Stack>

        <IconButton onClick={handleCloseModal} className={cx('buttonClose')}>
          <Avatar
            src={icons.commonIconCancel}
            className={cx('iconClose')}
            alt={t('common_alt_img', { ns: 'common/base' })}
          />
        </IconButton>
      </DialogTitle>

      <DialogContent className={cx('content')} dividers>
        <Grid2 columnSpacing={'24px'} rowSpacing={'24px'} container>
          <Grid2 size={6}>
            <Controller
              control={control}
              name={'title'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseInput
                    height={36}
                    label={t('form_add_quarantine_field_title')}
                    value={value}
                    onChange={onChange}
                    errorMessage={errors?.title?.message}
                  />
                );
              }}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              control={control}
              name={'type'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseSelect
                    gap={10}
                    height={36}
                    styleType='contained'
                    label={t('form_add_quarantine_field_type')}
                    placeholder={t('common_text_placeholder_select', { ns: 'common/base' })}
                    callApiFn={fetchOptionsQuarantineType}
                    value={value}
                    onChange={(data) => onChange(data?.value)}
                    errorMessage={errors?.title?.message}
                  />
                );
              }}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              control={control}
              name={'startDate'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseDatePicker
                    mode='date'
                    label={t('form_add_quarantine_field_start_date')}
                    value={value}
                    onChange={onChange}
                    errorMessage={errors.startDate?.message}
                  />
                );
              }}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              control={control}
              name={'endDate'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseDatePicker
                    mode='date'
                    label={t('form_add_quarantine_field_end_date')}
                    value={value}
                    onChange={onChange}
                    errorMessage={errors.endDate?.message}
                  />
                );
              }}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              control={control}
              name='inventoryId'
              render={({ field: { onChange, value } }) => (
                <BaseSelect<IOptionProductItem>
                  isSearch
                  type='loadMore'
                  gap={10}
                  height={36}
                  styleType='contained'
                  label={t('form_add_quarantine_field_product')}
                  placeholder={t('common_text_placeholder_select', { ns: 'common/base' })}
                  callApiFn={fetchOptionsProduct}
                  value={value}
                  disabled={!!idQuarantine}
                  onChange={(selected) => {
                    onChange(selected?.value);

                    setValue('variantId', selected?.variantId || EMPTY_STRING, {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                    setValue('productId', selected?.productId || EMPTY_STRING, {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                  }}
                  itemNameDefault={productNameDefault}
                  errorMessage={errors?.inventoryId?.message}
                  required
                />
              )}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              control={control}
              name={'quantity'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseInput
                    height={36}
                    type={'number'}
                    label={t('form_add_quarantine_field_quantity')}
                    value={String(value)}
                    onChange={onChange}
                    errorMessage={errors?.quantity?.message}
                    required
                  />
                );
              }}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              control={control}
              name={'description'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseInput
                    multiline
                    rows={7}
                    height={'auto'}
                    type={'number'}
                    label={t('form_add_quarantine_field_description')}
                    value={value || EMPTY_STRING}
                    onChange={onChange}
                    errorMessage={errors?.description?.message}
                  />
                );
              }}
            />
          </Grid2>
          <Grid2 size={6}>
            <Controller
              control={control}
              name={'quarantineFile'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseUploadDocument
                    label={t('form_add_quarantine_field_attachment')}
                    uploadedFiles={value}
                    onChange={(files: IBaseFile[]) => onChange(files)}
                    onRemove={handleRemoveFile}
                    errorMessage={errors.quarantineFile?.message}
                  />
                );
              }}
            />
          </Grid2>
        </Grid2>
      </DialogContent>

      <DialogActions className={cx('groupButton')}>
        <Button variant='outlined' className={cx('buttonCancel')} onClick={handleCloseModal}>
          {t('common_action_cancel', { ns: 'common/base' })}
        </Button>

        <Button
          variant='contained'
          className={cx('buttonSave')}
          onClick={handleSubmit(handleFormQuarantine)}
          disabled={idQuarantine ? !isDirty : false}
        >
          {t('common_action_save', { ns: 'common/base' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuarantineFormModal;
